// Hand-recorded window arrangements, made in the Layout Studio
// (`?layout`, a standalone tool with no server/boot) and pasted here.
// Multiple entries may share a width/height: they're VARIANTS of the
// same room, and a session picks one at random.
//
// Shape: { width, height,            // the resolution it was made at
//          self: {x, y},             // your window's center offset
//          neighbors: [{x, y}, …] }  // one per seat; LENGTH sets how
//                                    // many neighbors this room shows.
// Coordinates are window-CENTER offsets from the viewport center, in
// zoomed CSS px — exactly what PositionedWindow's `position` takes.
//
// The loader never demands an exact match: chooseLayout picks the
// nearest recorded size and applyLayout stretches its offsets to the
// real viewport (scale clamped so rooms neither crunch nor fly apart)
// and then clamps every window to reachability — a short window gets a
// gracefully squashed version of the nearest room, not a broken one.

export const RECORDED_LAYOUTS = [
    {"width":1280,"height":660,"self":{"x":0,"y":0},"neighbors":[{"x":-274,"y":-98},{"x":342,"y":-85},{"x":407,"y":172}]},
    {"width":1512,"height":830,"self":{"x":0,"y":0},"neighbors":[{"x":-399,"y":-169},{"x":-276,"y":156},{"x":417,"y":-193},{"x":90,"y":-112},{"x":580,"y":250}]},
    {"width":1920,"height":950,"self":{"x":0,"y":0},"neighbors":[{"x":-466,"y":-202},{"x":126,"y":-151},{"x":764,"y":-244},{"x":-593,"y":255},{"x":644,"y":268}]},
    {"width":2560,"height":1300,"self":{"x":0,"y":9},"neighbors":[{"x":-597,"y":-135},{"x":-772,"y":-409},{"x":88,"y":-329},{"x":-744,"y":261},{"x":934,"y":-415},{"x":40,"y":373},{"x":773,"y":319},{"x":873,"y":32}]},
    {"width":1600,"height":520,"self":{"x":-228,"y":-8},"neighbors":[{"x":589,"y":137},{"x":220,"y":-42}]},
];

// chooseLayout: nearest recorded layout by size; random among layouts
// of the SAME winning size (variants). rand is injectable for tests.
export function chooseLayout(w, h, layouts = RECORDED_LAYOUTS, rand = Math.random) {
  if (!layouts.length) return null;
  let bestScore = Infinity;
  for (const l of layouts) {
    const score =
      Math.abs(Math.log(w / l.width)) + Math.abs(Math.log(h / l.height));
    if (score < bestScore) bestScore = score;
  }
  const winners = layouts.filter(
    (l) =>
      Math.abs(Math.log(w / l.width)) + Math.abs(Math.log(h / l.height)) ===
      bestScore,
  );
  return winners[Math.floor(rand() * winners.length) % winners.length];
}

// How far offsets stretch before we'd rather let clamping take over —
// unbounded scaling would fling a 1280px room across a 5120px wall.
const MIN_STRETCH = 0.6;
const MAX_STRETCH = 1.5;

// applyLayout: fit a recorded layout to the actual viewport. Pure —
// the caller supplies every dimension. Returns {self, neighbors} in
// PositionedWindow coordinates (center offsets from viewport center).
// Clamp rules mirror PositionedWindow's live-drag clamp: title bar
// never above the screen top or under the taskbar, a hand's width
// stays grabbable horizontally.
export function applyLayout(layout, { W, H, winW, winH, taskbar }) {
  const sx = Math.min(MAX_STRETCH, Math.max(MIN_STRETCH, W / layout.width));
  const sy = Math.min(MAX_STRETCH, Math.max(MIN_STRETCH, H / layout.height));
  const GRAB_X = 60;
  const clamp = (p) => {
    let cx = W / 2 + p.x * sx;
    let cy = H / 2 + p.y * sy;
    cx = Math.max(GRAB_X - winW / 2, Math.min(W - GRAB_X + winW / 2, cx));
    cy = Math.max(winH / 2, Math.min(H - taskbar - 24 + winH / 2, cy));
    return { x: Math.round(cx - W / 2), y: Math.round(cy - H / 2) };
  };
  return {
    self: clamp(layout.self ?? { x: 0, y: 0 }),
    neighbors: layout.neighbors.map(clamp),
  };
}
